// pages/sections/TechFeeCalcModal.js
// "Tech Fee Calc" (STEERING_USERS / admins): the global tech fee settings for the Client Quoted Fee Review, one row per
// fee type. The fee for a job is the minimum, or — when % of its WIP time comes to more — the minimum plus the excess
// rounded up in $50 steps. It shows in each row's "Expected Tech Fee" column and is added on top of the partner's
// quote. 0 / 0 means no tech fee. Saved through api/diQuotedFeeTechFee; the section refetches so every row updates.

import React, { useState } from 'react';
import Modal from '../../components/Modal';
import { postJson } from './reviewUtil';

const TYPES = [['tax', 'Tax'], ['aa', 'A&A']];

// "$1,200.50" / "1.25%" / " 12 " → number, or NaN for blank / junk.
const parse = text => {
  const cleaned = String(text ?? '').replace(/[$,%\s]/g, '');
  return cleaned === '' ? NaN : Number(cleaned);
};
const show = n => (n == null || !Number.isFinite(Number(n)) ? '' : String(Number(n)));

export default function TechFeeCalcModal({ period, settings, onClose, onSaved }) {
  const [vals, setVals] = useState(() => Object.fromEntries(
    TYPES.map(([k]) => [k, { minimum: show(settings?.[k]?.minimum), pct: show(settings?.[k]?.pct) }])));
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, field, text) => setVals(prev => ({ ...prev, [k]: { ...prev[k], [field]: text } }));

  async function save(e) {
    e.preventDefault();
    if (saving) return;
    const body = { period };
    for (const [k] of TYPES) body[k] = { minimum: parse(vals[k].minimum), pct: parse(vals[k].pct) };
    setSaving(true);
    setError('');
    try {
      await postJson('/api/diQuotedFeeTechFee', body);
      await onSaved();
    } catch (err) {
      setError(err.message || 'The tech fee settings could not be saved.');
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={saving ? undefined : onClose}
      title="Tech Fee Calc"
      footer={(
        <>
          <button type="button" className="rt-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="qf-techcalc-form" className="rt-btn rt-btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    >
      <form id="qf-techcalc-form" onSubmit={save}>
        <table className="qf-techcalc">
          <thead>
            <tr><th /><th>Minimum</th><th>% of WIP</th></tr>
          </thead>
          <tbody>
            {TYPES.map(([k, label]) => (
              <tr key={k}>
                <td><b>{label}</b></td>
                <td>
                  <div className="qf-input-wrap">
                    <span className="qf-dollar">$</span>
                    <input className="qf-input" inputMode="decimal" autoComplete="off" aria-label={`${label} minimum`}
                           value={vals[k].minimum} onChange={e => set(k, 'minimum', e.target.value)} disabled={saving} />
                  </div>
                </td>
                <td>
                  <div className="qf-input-wrap">
                    <input className="qf-input" inputMode="decimal" autoComplete="off" aria-label={`${label} % of WIP`}
                           value={vals[k].pct} onChange={e => set(k, 'pct', e.target.value)} disabled={saving} />
                    <span className="qf-pct">%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="qf-note">
          The expected tech fee is the <b>minimum</b>, unless the <b>% of the job&apos;s WIP time</b> comes to more: then it is the
          minimum plus the excess, rounded up in $50 steps (minimum $100 at 1%: $10,000 of WIP → $100, $10,001 → $150).
          A job with no WIP or no billing gets no tech fee. It is added on top of the fee the partner enters, and the
          total is what goes to Knuula. Leave both at 0 for no tech fee. Quotes already submitted are not changed.
        </p>
        {error && <p className="qf-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
